import { hideNativeScrollbarClass } from "@/components/layout/contentLayout.tsx";
import { panelToolbarControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import type { PanelControlDimension } from "@/views/Settings/panelControls.tsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";

/**
 * One segment's box, matching {@link panelToolbarControlClass}: the header's
 * desktop scale, held at that size on every screen rather than stepping up on a
 * phone the way the header's own controls do.
 *
 * Height rather than `size-*` because a segment is as wide as its word. The
 * point of matching the header is that a promoted axis reads as part of the same
 * family of controls as the glyphs beside it, rather than as a row of buttons
 * that happens to sit above the list — and the smaller box is what lets a
 * ten-option axis fit a row at all. That matters most on a phone, which is
 * exactly where a thumb-sized box would push the options out of reach, so this
 * row holds one size throughout. 28px still clears the 24px WCAG 2.5.8 target
 * minimum the switch in `toggle.tsx` is sized against.
 */
const segmentClass = "h-7 px-2.5 text-xs";

/** How much of the row one press of an arrow travels: most of a screenful, with overlap to keep a landmark in view. */
const SCROLL_STEP = 0.8;

/**
 * The control that reaches the segments past an edge.
 *
 * An arrow rather than the native scrollbar, which is hidden here: the bar sits
 * under the row and reads as a rule across the toolbar, and on a mouse it is the
 * one input that cannot scroll sideways at all. A trackpad and a touchscreen can
 * swipe, and tabbing scrolls the focused segment into view on its own, so this
 * exists for the pointer that has no other way there.
 *
 * Carries no surface of its own — no fill, no shadow — so it reads as part of
 * the edge treatment rather than as a button parked on top of one. It can afford
 * that because it sits inside the strip the row's mask clears, where the
 * segments behind it are already gone; a raised control would be the loudest
 * thing on a row whose whole job is to stay quiet until looked at. Hovering
 * fills it, which is where the affordance lives.
 *
 * Floats over the row rather than sitting beside it: taking layout width would
 * change how much the row can show, which changes whether it overflows, which
 * changes whether this is drawn — a loop that settles only by luck.
 *
 * Always rendered, and shown or hidden by `.panel-segments-arrow` in
 * `index.css`, which reads the row's scroll position directly. Being `hidden`
 * rather than clear is what keeps an arrow with nowhere to go out of the tab
 * order and away from a screen reader.
 */
const ScrollArrow = ({
  direction,
  axisLabel,
  statsigName,
  onClick,
}: {
  direction: "start" | "end";
  axisLabel: string;
  statsigName: string;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className={cn(
      panelToolbarControlClass,
      "panel-segments-arrow absolute top-1/2 z-10 -translate-y-1/2 text-muted-foreground",
      direction === "start"
        ? "panel-segments-arrow-start left-0"
        : "panel-segments-arrow-end right-0",
    )}
    aria-label={
      direction === "start"
        ? `Show earlier ${axisLabel.toLowerCase()} options`
        : `Show more ${axisLabel.toLowerCase()} options`
    }
    data-statsig-name={`${statsigName}-scroll-${direction}`}
    onClick={onClick}
  >
    {direction === "start" ? <ChevronLeft /> : <ChevronRight />}
  </Button>
);

/**
 * One filter axis spelled out in full, as a row of segments instead of a menu
 * behind a glyph.
 *
 * The selected segment is filled and the rest stay naked — no border, no fill,
 * muted ink. Outlined chips would put a box around every option and, at four or
 * ten of them, read as a fence across the top of the list; leaving the unchosen
 * bare means the fill alone carries the selection, so the row is quiet until the
 * eye looks for it.
 *
 * Composed from two `Button` variants rather than given a variant of its own:
 * `secondary` is already `bg-accent` over dark ink, and `ghost` is already the
 * transparent box that fills on hover. The only thing this adds is the resting
 * mute on the unchosen, which is what makes them read as options rather than as
 * a row of buttons waiting to be pressed.
 *
 * Whether the row runs past either edge is not computed here. The row publishes
 * a scroll timeline and its fade and arrows animate along it, so the answer is
 * the scroll position itself rather than a measurement taken beside it — see
 * `.panel-segments` in `index.css`. Scrolling on a press stays here, because
 * that is an interaction and not a size.
 *
 * Only for an axis whose options are few and short enough to sit in a row. The
 * page decides that per axis via `PanelControlDimension.display`; an axis of
 * email addresses cannot, and stays in the menu.
 */
export const PanelSegmentedFilter = ({ axis }: { axis: PanelControlDimension }) => {
  const rowRef = useRef<HTMLFieldSetElement>(null);
  const scrollBy = (direction: 1 | -1) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * SCROLL_STEP, behavior: "smooth" });
  };

  return (
    <div className="panel-segments relative flex min-w-0 flex-1 items-center">
      <ScrollArrow
        direction="start"
        axisLabel={axis.label}
        statsigName={axis.statsigName}
        onClick={() => scrollBy(-1)}
      />
      {/* A `fieldset` rather than a div with `role="group"` — the native grouping
          element, which is what `ButtonGroup` in the design system uses too, and
          it needs the same three resets to stop carrying a border and its own
          spacing.

          Grouped buttons rather than radios: these narrow a list in place, and
          `aria-pressed` says which one is doing it. Radio semantics would
          promise arrow-key roving focus this doesn't implement, and would
          announce a form control the page never submits.

          Scrolls rather than wraps — Search offers ten types, and a second line
          would push the list down the page every time the row grew.

          `-mx-1 px-1` and `-my-1 py-1` both buy room for a segment's focus ring:
          `overflow-x: auto` resolves `overflow-y` to `auto` as well, so without
          the vertical pair the ring is clipped above and below while the row
          shows only its left and right fragments. */}
      <fieldset
        ref={rowRef}
        aria-label={axis.label}
        className={cn(
          "panel-segments-row -mx-1 -my-1 flex min-w-0 items-center gap-1 overflow-x-auto border-none px-1 py-1",
          hideNativeScrollbarClass,
        )}
      >
        {axis.options.map((option) => {
          const selected = option.value === axis.value;
          return (
            <Button
              key={option.value}
              type="button"
              size="fit"
              variant={selected ? "secondary" : "ghost"}
              aria-pressed={selected}
              onClick={() => axis.onChange(option.value)}
              data-statsig-name={`${axis.statsigName}-${option.value}`}
              // `shrink-0` so a long row scrolls instead of squeezing its
              // segments into unreadable slivers.
              className={cn(segmentClass, "shrink-0", !selected && "text-muted-foreground")}
            >
              {option.label}
              {option.count === undefined ? null : (
                <>
                  {" "}
                  <span className="tabular-nums text-muted-foreground">{option.count}</span>
                </>
              )}
            </Button>
          );
        })}
      </fieldset>
      <ScrollArrow
        direction="end"
        axisLabel={axis.label}
        statsigName={axis.statsigName}
        onClick={() => scrollBy(1)}
      />
    </div>
  );
};
